<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  DBtn,
  DTextField,
  DTable,
  Edit03Icon,
  Trash03Icon,
  SearchMdIcon,
  XIcon,
  DModal,
  DLoading
} from 'front-library'
import DynamicForm from './DynamicForm.vue'
import Popper from 'vue3-popper'
import { Field, getForm } from '@/app/core/services/forms'
import { snakeToCamelObject } from '@/app/core/util/strings'
import useDynamicTable from './useDynamicTable'
import { validateTranslate } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'

const {
  search,
  columns,
  rows,
  perPage,
  offset,
  totalRows,
  sortBy,
  loadData,
  loadRow,
  setField,
  replaceIdsByName,
  download,
  formatNumbers,
  loadingReport,
  messageLoading,
  showModal,
  typeAlertModal,
  titleModal,
  descriptionModal,
  closeModalReport,
  urlFile
} = useDynamicTable()

const props = defineProps<{
  field: Field
  subForm?: string
  readonly?: boolean
  label?: any
  modelValue?: any
  allModel?: any
  error?: any
  actions?: any
  disabled?: any
  options?: any
  class?: any
}>()

const emit = defineEmits(['update:modelValue', 'filter', 'file'])
const route = useRoute()
const { t } = useI18n()

const customForm = ref<any>({})
const updatedRows = ref<any[]>([])
const rowModel = ref<any>({})
const isOpenModal = ref(false)
const modalTitle = ref('')
const isReadOnly = ref(false)
const currentId = ref('')
const titleBtnSave = ref('')
const customRows = ref<any[]>([])
const form = ref<typeof DynamicForm | null>(null)
const labelTranslate = ref(validateTranslate(props.label ?? ''))

// Datos componente Loading
const loading = ref(false)

// Temporizador search
let debounceTimeout: any

const debouncedLoadData = () => {
  clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    loading.value = true
    if (search.value !== undefined && search.value !== null) {
      offset.value = 0
      loadData(currentId.value)
    }
  }, 500) // medio segundo
}

// Watchers
watch(
  () => updatedRows.value.length,
  () => {
    void updateViewRows()
    emitUpdate()
  }
)
watch(
  () => rows.value,
  () => {
    void updateViewRows()
  }
)
watch(
  () => search.value,
  () => {
    debouncedLoadData()
  }
)

const updateViewRows = async () => {
  const cloneUpdatedRows = JSON.parse(JSON.stringify(updatedRows.value))
  const rowsIds = cloneUpdatedRows.map((_: any) => _.id)
  const updatedRowsUnreplaced: any[] = [
    ...cloneUpdatedRows,
    ...rows.value.filter(_ => !rowsIds.includes(_.id))
  ]
  const updatedRowReplaced: any[] = []
  for (const row of updatedRowsUnreplaced) {
    updatedRowReplaced.push(await replaceIdsByName(row))
  }
  customRows.value = updatedRowReplaced
  loading.value = false
}

const actionsConf = new Map<string, any>([
  [
    'added',
    {
      class: 'text-green-600 bg-green-100',
      dot: 'bg-green-600',
      text: t('form.button.addRegistry'),
      tooltip: t('form.button.cancelRegistration')
    }
  ],
  [
    'edited',
    {
      class: 'text-blue-600 bg-blue-100',
      dot: 'bg-blue-600',
      text: t('form.button.editRegistry'),
      tooltip: t('form.button.cancelEdit')
    }
  ],
  [
    'removed',
    {
      class: 'text-red-600 bg-red-100',
      dot: 'bg-red-600',
      text: t('form.button.deleteRecord'),
      tooltip: t('form.button.cancelDeletion')
    }
  ]
])

const add = async () => {
  loading.value = true
  titleBtnSave.value = t('form.button.save')
  rowModel.value = {
    action: 'added'
  }
  modalTitle.value = `${t('form.button.add')} ${labelTranslate.value}`
  customForm.value = await getForm({ entity: props.subForm ?? '' })
  isOpenModal.value = true
  // emit('update:modelValue', updatedRows)
  loading.value = false
}

const saveAdd = async (values: any) => {
  values.action = values.id ? 'edited' : 'added'
  values.actionId = values.id || Math.random()
  formatNumbers([values])
  updatedRows.value.push(values)
  isOpenModal.value = false
  loading.value = true
}

const update = async (id: any, currentId?: string) => {
  loading.value = true
  titleBtnSave.value = t('form.button.update')
  rowModel.value = await loadRow(id, currentId)
  modalTitle.value = `${t('table.action.edit')} ${labelTranslate.value}`
  customForm.value = await getForm({ entity: props.subForm ?? '' })
  isOpenModal.value = true
  loading.value = false
}

const get = async (id: any, currentId?: string) => {
  loading.value = true
  titleBtnSave.value = ''
  rowModel.value = await loadRow(id, currentId)
  modalTitle.value = `${t('table.action.detail')} ${labelTranslate.value}`
  customForm.value = await getForm({ entity: props.subForm ?? '' })
  isReadOnly.value = true
  isOpenModal.value = true
  loading.value = false
}

const closeModal = () => {
  isReadOnly.value = false
  isOpenModal.value = false
}

const remove = (values: any) => {
  loading.value = true

  let newValues = { ...rows.value.find(_ => _.id === values.id) }
  newValues.action = 'removed'
  newValues.actionId = Math.random()
  newValues = deleteFields(newValues)
  updatedRows.value.push(newValues)
}

const deleteFields = (obj: any) => {
  const columnNames = new Set(columns.value.map((column: any) => column.name))
  columnNames.add('action')
  columnNames.add('actionId')
  columnNames.add('id')
  for (const key in obj) {
    if (!columnNames.has(key)) {
      delete obj[key]
    }
  }
  return obj
}

const cancelAction = (actionId: string) => {
  loading.value = true
  updatedRows.value = updatedRows.value.filter(
    (row: any) => row.actionId !== actionId
  )
}

const emitUpdate = () => {
  const newObj = JSON.parse(JSON.stringify(updatedRows.value))
  emit(
    'update:modelValue',
    newObj.map((row: any) => {
      delete row.actionId
      delete row.createdAt
      delete row.updatedAt
      for (let key in row) {
        if (
          typeof row[key] === 'string' &&
          (row[key].match(/^[\d,]+\.\d{2}$/) || row[key].match(/%/))
        ) {
          let cleaned = row[key].replace(/,/g, '')
          cleaned = cleaned.replace(/\.00$/, '')
          cleaned = cleaned.replace(/%$/, '')
          row[key] = cleaned
        }
      }
      return snakeToCamelObject(row)
    })
  )
}

onMounted(async () => {
  setField(props.field)
  currentId.value = route.params.id as string
  await loadData(currentId.value)
})
</script>
<template>
  <div class="sm:col-span-6 lg:col-span-6 xl:col-span-6" :class="class">
    <DLoading v-show="loadingReport" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      :acceptQuestion="
        t('modal.acceptQuestion', {
          action: t('table.action.delete')
        })
      "
      @closeModal="closeModalReport">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <DLoading v-show="loading" :message="t('loading.data')" />
    <div>
      <DModal
        :open="isOpenModal"
        :title="modalTitle"
        size="md:w-full lg:w-full xl:w-4/6"
        :form="true"
        @keydown.esc="closeModal">
        <template #form>
          <div class="text-lg text-gray-800 font-semibold">
            {{ modalTitle }}
          </div>
          <DynamicForm
            ref="form"
            v-if="isOpenModal"
            :form-schema="customForm"
            :initial-model="rowModel"
            :readonly="isReadOnly"
            :height-form-modal="true"
            @cancel="closeModal"
            :title-btn-save="titleBtnSave"
            @submit="saveAdd" />
        </template>
        <template #actions>
          <div></div>
        </template>
      </DModal>
      <div class="bg-white pb-4 w-full">
        <div class="sm:flex justify-between">
          <div class="flex mb-3 sm:mb-0 sm:w-2/3 md:w-1/2 lg:w-5/12">
            <DTextField
              :placeholder="t('form.button.search') + '...'"
              :icon="'SearchLgIcon'"
              class="mr-2 w-full"
              v-model="search" />
          </div>
          <div class="flex">
            <DBtn
              v-if="field?.props?.actions.includes('download')"
              type="button"
              @click="download()"
              class="bg-green-600 hover:bg-green-700"
              v-html="t('form.button.download')" />
            <DBtn
              v-if="field?.props?.actions.includes('add') && !readonly"
              :icon="'PlusIcon'"
              type="button"
              @click="add()">
              {{ t('form.button.add') }}
            </DBtn>
          </div>
        </div>
      </div>
      <DTable
        :columns="columns"
        :rows="customRows"
        :total-rows="totalRows"
        :select-options="false"
        :text-no-data="t('table.noData')"
        :text-per-page="t('table.rowsPerPage')"
        :table-scroll="false"
        @perPage=";(perPage = $event), loadData(currentId)"
        @offset="offset = $event"
        @sort=";(sortBy = $event), loadData(currentId)">
        <template v-if="!readonly" #actions="data: any">
          <div class="flex py-2.5 justify-end gap-1">
            <Popper
              v-if="data.action"
              arrow
              offsetDistance="12"
              :content="actionsConf.get(data.action)?.tooltip"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3 cursor-pointer"
                :class="actionsConf.get(data.action)?.class"
                @click="cancelAction(data.actionId)">
                <div
                  class="mr-2 w-1.5 h-1.5 rounded-full"
                  :class="actionsConf.get(data.action)?.dot"></div>
                {{ actionsConf.get(data.action)?.text }}
                <XIcon />
              </div>
            </Popper>
            <Popper
              v-if="field?.props?.actions.includes('view') && !data.action"
              arrow
              offsetDistance="12"
              :content="t('table.action.detail')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="bg-gray-50 rounded-md py-2"
                @click="get(data.id, currentId)">
                <SearchMdIcon
                  class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              </div>
            </Popper>
            <Popper
              v-if="field?.props?.actions.includes('edit') && !data.action"
              arrow
              offsetDistance="12"
              :content="t('table.action.edit')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="bg-gray-50 rounded-md py-2"
                @click="update(data.id, currentId)">
                <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              </div>
            </Popper>
            <Popper
              v-if="field?.props?.actions.includes('delete') && !data.action"
              arrow
              offsetDistance="12"
              :content="t('table.action.remove')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div class="bg-gray-50 rounded-md py-2" @click="remove(data)">
                <Trash03Icon
                  class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              </div>
            </Popper>
          </div>
        </template>
      </DTable>
    </div>
  </div>
</template>
